import { Component, EventEmitter, Input, Output, ViewEncapsulation, computed, signal, viewChild } from '@angular/core';
import { PmModalComponent, type PmModalFieldNav } from '../pm-modal/pm-modal.component';
import { PmFormSectionComponent } from '../pm-form-section/pm-form-section.component';
import { PmConfirmDialogComponent } from '../pm-confirm-dialog/pm-confirm-dialog.component';
import { PmPhoneInputComponent } from '../../pm-phone-input/pm-phone-input.component';
import { PmDatePickerComponent } from '../../pm-date-picker/pm-date-picker.component';
import { PM_COUNTRY_DIAL_CODES } from '../../pm-phone-input/pm-country-dial-codes';
import { clientTypeSupportsDirectors } from '../../../../core/utils/client-form-validators.util';
import {
  PmFormCustomSelectComponent,
  type PmFormCustomSelectOption
} from '../../pm-form-custom-select/pm-form-custom-select.component';

/**
 * One row of the Directors & Shareholders block — the part of the live form the Figma spec has no
 * place for. Kept as a repeating list rather than a fixed set of fields because a client can have
 * any number of them, each with its own appointment and termination dates.
 */
export interface PmClientOfficer {
  /** Stable across edits and removals, so @for can track rows without reusing DOM between people. */
  key: string;
  name: string;
  address: string;
  email: string;
  mobile: string;
  country: string;
  shareholder: boolean;
  appointedOn: string;
  terminatedOn: string;
}

/** The officer fields marked * (Termination Date and Shareholder stay optional). */
type PmOfficerRequiredField = 'name' | 'email' | 'address' | 'mobile' | 'country' | 'appointedOn';

const OFFICER_REQUIRED_FIELDS: readonly { field: PmOfficerRequiredField; label: string }[] = [
  { field: 'name', label: 'Name' },
  { field: 'email', label: 'Email' },
  { field: 'address', label: 'Address' },
  { field: 'mobile', label: 'Mobile' },
  { field: 'country', label: 'Country' },
  { field: 'appointedOn', label: 'Appointment Date' }
];

/** A person in the firm's contact book. The picker shows the name; choosing one fills the rest. */
export interface PmClientContact {
  id: string;
  name: string;
  email: string;
  phone: string;
  /** ISO for the phone field's country picker. */
  countryIso: string;
}

/**
 * A practice-defined field. Every firm configures its own, so the popup cannot know them ahead of
 * time - it is handed the definitions and renders whatever it is given.
 */
export interface PmCustomField {
  /** The stored id (PM_CustomField.Id), which is also the key its value is held under. */
  id: number;
  name: string;
  description?: string;
  /** 'text' | 'number' | 'date' | 'dropdown' - anything else falls back to text. */
  fieldType?: string;
  required?: boolean;
  dropdownOptions?: readonly string[];
}

/** One tickable service in the "Select Services" grid. */
export interface PmServiceOption {
  id: string;
  label: string;
}

/**
 * Add New Client — the app's largest form popup, built straight from the Figma spec.
 *
 * Structure only: every control is an uncontrolled input with the design's placeholder. Wiring it
 * to real data is the consuming page's job, which is why this takes option lists in and emits
 * intent out rather than reaching for an API itself.
 *
 * The shell, the section cards and the header all come from <pm-modal> and <pm-form-section>, so
 * what is left here is this popup's own field layout and nothing else.
 */
@Component({
  selector: 'pm-add-client-modal',
  standalone: true,
  imports: [
    PmModalComponent,
    PmFormSectionComponent,
    PmFormCustomSelectComponent,
    PmPhoneInputComponent,
    PmConfirmDialogComponent,
    PmDatePickerComponent
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './pm-add-client-modal.component.html',
  styleUrls: ['./pm-add-client-modal.component.css', '../pm-form-field.css']
})
export class PmAddClientModalComponent {
  @Input() title = 'Add New Client';
  @Input() subtitle = 'Fill in the details below to add a new client.';

  /** Dropdown contents. Defaults are the values in the design so the popup previews as drawn. */
  @Input() clientTypes: string[] = ['Limited', 'Sole Trader', 'Partnership', 'LLP', 'Individual', 'Trust', 'Charity'];
  @Input() vatSchemes: string[] = [
    'Accural Based',
    'Non-VAT Registered',
    'Flat Rate Accural Based',
    'Cash Based Normal Scheme',
    'Flat Rate Cash Based'
  ];
  @Input() billingCycles: string[] = ['Monthly', 'Quarterly', 'Yearly'];
  @Input() sources: string[] = [
    'Google', 'Linkedin', 'Facebook', 'Twitter', 'Accountex', 'AccountingWEB',
    'HMRC Website', 'Received an email', 'Friend/Referral', 'Blog', 'Live Chat', 'Others'
  ];

  @Input() services: PmServiceOption[] = [
    { id: 'accounts', label: 'Accounts' },
    { id: 'vat', label: 'VAT Returns' },
    { id: 'sa-tax', label: 'SA TAX Returns' },
    { id: 'ct600', label: 'CT600' },
    { id: 'payroll', label: 'Payroll' },
    { id: 'bookkeeping', label: 'Book Keeping' },
    { id: 'confirmation', label: 'Confirmation statement' },
    { id: 'cis', label: 'CIS' }
  ];

  /** The header counter. `completedSteps` is the caller's own judgement of progress — this popup
   *  does not validate, so it cannot work the number out for itself. */
  /**
   * Held by the caller for as long as the save is in flight. The popup does not set it itself —
   * it does not do the saving, and a flag it flipped on its own would stay on if the request
   * failed and the caller wanted the form left open to correct something.
   *
   * While it is true the button spins, the footer is inert and the modal cannot be dismissed, so
   * a half-written client cannot be abandoned mid-request.
   */
  @Input() saving = false;

  @Input() completedSteps = 0;
  @Input() totalSteps = 6;

  /** The shell owns the walk to a missing field: the scroll, the focus and opening the picker. */
  private readonly modal = viewChild.required(PmModalComponent);

  readonly stepDots = computed(() => Array.from({ length: this.totalSteps }, (_v, i) => i));

  @Output() closed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() savedDraft = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();
  /** The magnifier beside Company Name — a Companies House lookup on the real popup. */
  @Output() lookupCompany = new EventEmitter<void>();


  /** Plain string lists in, <pm-form-custom-select> options out. The inputs stay strings because
   *  that is what a caller has to hand; the value and the label are the same thing here. */
  private static toOptions(values: readonly string[]): PmFormCustomSelectOption[] {
    return values.map((v) => ({ value: v, label: v }));
  }

  readonly clientTypeOptions = computed(() => PmAddClientModalComponent.toOptions(this.clientTypes));
  readonly vatSchemeOptions = computed(() => PmAddClientModalComponent.toOptions(this.vatSchemes));
  readonly billingCycleOptions = computed(() => PmAddClientModalComponent.toOptions(this.billingCycles));
  readonly sourceOptions = computed(() => PmAddClientModalComponent.toOptions(this.sources));
  readonly statusOptions = PmAddClientModalComponent.toOptions(['Active', 'Inactive']);
  /**
   * Sample options for the pickers that have no real source yet, so the preview shows the shape a
   * populated dropdown takes rather than an empty box. Replace them from the caller when this is
   * wired up — they are @Inputs for exactly that reason.
   */
  /** Supplied by the host. Empty by default - a picker that lists people who do not exist is
   *  worse than an empty one. */
  @Input() contactBook: PmClientContact[] = [];

  /** Name, with the dialable number beside it and the email underneath. The rest of the record is
   *  used on selection, to fill the fields below the picker. */
  readonly contacts = computed<PmFormCustomSelectOption[]>(() =>
    this.contactBook.map((c) => ({
      value: c.id,
      label: c.name,
      meta: PmAddClientModalComponent.dialable(c),
      hint: c.email
    }))
  );

  /** The stored number is national-only, so the country picker owns the prefix - it is put back
   *  here because a number without its dial code is not one anybody can ring. */
  private static dialable(contact: PmClientContact): string {
    const dial = PM_COUNTRY_DIAL_CODES.find((d) => d.iso === contact.countryIso)?.dial ?? '';
    return `${dial} ${contact.phone}`.trim();
  }

  @Input() staff: PmFormCustomSelectOption[] = [];

  /** Practice-defined fields, rendered in their own section. Empty means the section is not shown
   *  at all - an "Additional Fields" heading over nothing is worse than no heading. */
  @Input() customFields: readonly PmCustomField[] = [];

  /** Values keyed by custom field id, held by the host alongside the rest of the draft. */
  @Input() customFieldValues: Record<number, string> = {};

  @Output() customFieldChanged = new EventEmitter<{ id: number; value: string }>();

  customFieldValue(id: number): string {
    return this.customFieldValues[id] ?? '';
  }

  setCustomFieldValue(id: number, value: string): void {
    this.dirty.set(true);
    this.customFieldChanged.emit({ id, value });
  }

  customFieldOptions(field: PmCustomField): PmFormCustomSelectOption[] {
    return (field.dropdownOptions ?? []).map((o) => ({ value: o, label: o }));
  }

  /** The dom id for a custom field, kept in the same namespace as the fixed ones so Save can walk
   *  to it the same way. */
  customFieldDomId(id: number): string {
    return this.fieldDomId(`acm-custom-${id}`);
  }

  customFieldError(id: number): string {
    return this.errorFor(`acm-custom-${id}`);
  }





  /** The country list already exists for the phone picker, so it is reused rather than retyped. */
  readonly countryOptions = PM_COUNTRY_DIAL_CODES.map((c) => ({ value: c.name, label: c.name }));


  readonly emptyOptions: PmFormCustomSelectOption[] = [];

  /**
   * Real option lists from the host, keyed by the field id they belong to.
   *
   * One input rather than a dozen: every picker in here asks the same question, and a page that
   * has real data for six of them should not have to know which six inputs to fill. Anything the
   * host does not supply falls back to sampleOptions() below, which is what the fixed lists
   * (client types, billing cycles, AML statuses) come from - the ones with no source of their
   * own on the page yet. Nothing here invents a person or a company.
   */
  @Input() optionSources: Record<string, readonly PmFormCustomSelectOption[]> | null = null;

  /**
   * The last few lists with no host source. Everything business-specific - segments, accounting
   * systems, AML statuses - was removed: the page defines those, and a shared popup guessing at
   * them is how Micro/Small/Medium turned up in a firm that grades clients Bronze to Platinum.
   */
  private sampleOptions(id: string): readonly PmFormCustomSelectOption[] {
    switch (id) {
      case 'acm-client-type': return this.clientTypeOptions();
      case 'acm-vat-scheme': return this.vatSchemeOptions();
      case 'acm-source': return this.sourceOptions();
      case 'acm-status':
      case 'acm-partner-role': return this.statusOptions;
      case 'acm-country': return this.countryOptions;
      case 'acm-manager':
      case 'acm-staff':
      case 'acm-partner': return this.staff;
      // Contacts and portal users have no sample list on purpose: an invented person is worse
      // than an empty picker, and the host is the only thing that knows the real ones.
      default: return this.emptyOptions;
    }
  }

  optionsFor(id: string): readonly PmFormCustomSelectOption[] {
    return this.optionSources?.[id] ?? this.sampleOptions(id);
  }

  /**
   * Which dropdown is open, and what each one holds.
   *
   * <pm-form-custom-select> deliberately does not own either — one open menu at a time is a
   * property of the form, not of a single control — so the popup keeps both in one place rather
   * than a pair of signals per field.
   */
  readonly openDropdown = signal<string | null>(null);
  /**
   * Sample values so the popup previews with something in it.
   *
   * Four of the starred fields are deliberately left blank — Post code, County, Contact Email and
   * Source — so Save has something to catch on. Replace the whole map from the caller when this is
   * wired to real data.
   */
  /**
   * The form's values, owned by the host when it binds them and by the popup when it does not.
   *
   * A page that already holds a client draft (the Clients screen holds ClientsAddFormDraft) needs
   * the popup to read and write that, not a second copy it keeps in sync - two sources of truth for
   * one form is how a field silently stops saving. The preview page binds nothing and gets the
   * sample data below.
   */
  @Input() set values(next: Record<string, string> | null) {
    if (next) this.hostValues.set(next);
  }

  /** Emitted on every edit, as { id, value } - the host applies it to its own draft. */
  @Output() valueChanged = new EventEmitter<{ id: string; value: string }>();

  private readonly hostValues = signal<Record<string, string> | null>(null);

  /** Only used when no host binds [values] - an empty form, not a filled-in sample one. Sample
   *  data belongs to whoever is demoing, not to a component the Clients page renders. */
  private readonly ownValues = signal<Record<string, string>>({});

  readonly fieldValues = computed(() => this.hostValues() ?? this.ownValues());


  onDropdownToggle(event: { id: string }): void {
    this.openDropdown.update((cur) => (cur === event.id ? null : event.id));
  }

  /** Only the unbound copy is mutated here; a host that owns the values applies valueChanged to
   *  its own draft and feeds the result back through [values]. */
  private writeValue(id: string, value: string): void {
    this.writeValues({ [id]: value });
  }

  private writeValues(patch: Record<string, string>): void {
    if (this.hostValues()) {
      for (const [k, v] of Object.entries(patch)) {
        if (k !== undefined) this.valueChanged.emit({ id: k, value: v });
      }
      return;
    }
    this.ownValues.update((prev) => ({ ...prev, ...patch }));
  }

  valueOf(id: string): string {
    return this.fieldValues()[id] ?? '';
  }

  setValue(id: string, value: string): void {
    this.dirty.set(true);

    // Picking a contact fills the name, email and phone beneath it. Whoever chose the person did
    // so to avoid retyping what the firm already holds - leaving the fields blank makes the picker
    // look like it did nothing.
    if (id === 'acm-contact') {
      const contact = this.contactBook.find((c) => c.id === value);
      if (contact) {
        this.writeValues({
          'acm-contact-name': contact.name,
          'acm-contact-email': contact.email,
          'acm-contact-phone-no': contact.phone,
          'acm-contact-phone-no-iso': contact.countryIso
        });
        this.errors.update((prev) => {
          const next = { ...prev };
          for (const k of ['acm-contact-name', 'acm-contact-email', 'acm-contact-phone-no']) delete next[k];
          return next;
        });
      }
    }
    this.writeValue(id, value);
    this.valueChanged.emit({ id, value });
    this.openDropdown.set(null);
    if (value.trim()) this.clearError(id);
  }

  /** Typing into a field answers its complaint straight away rather than leaving the message up
   *  until the next Save, which reads as the input having been ignored. */
  private clearError(id: string): void {
    if (!this.errors()[id]) return;
    this.errors.update((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    this.missingTotal.update((n) => Math.max(0, n - 1));
  }

  /**
   * The fields marked * in the design, in the order they appear on screen — which is the order
   * Save walks them in, so the first complaint is always the highest missing one.
   *
   * Ids are the control's own, not derived from the label: the dropdowns were given theirs when
   * they were written, and a second naming scheme would drift the moment a label changed.
   */
  private static readonly REQUIRED: readonly { id: string; label: string }[] = [
    { id: 'acm-company-client-id', label: 'Company/Client ID' },
    { id: 'acm-client-type', label: 'Client Type' },
    { id: 'acm-company-name', label: 'Company/Client Name' },
    { id: 'acm-primary-email', label: 'Primary Email' },
    { id: 'acm-address-line-1', label: 'Address Line 1' },
    { id: 'acm-city-town', label: 'City/Town' },
    { id: 'acm-post-code', label: 'Post code' },
    { id: 'acm-county', label: 'County' },
    { id: 'acm-country', label: 'Country' },
    { id: 'acm-contact', label: 'Select Contact' },
    { id: 'acm-address-type', label: 'Address Type' },
    { id: 'acm-contact-name', label: 'Contact Name' },
    { id: 'acm-contact-email', label: 'Contact Email' },
    { id: 'acm-contact-phone-no', label: 'Contact Phone No' },
    { id: 'acm-acc-system', label: 'Accounting System' },
    { id: 'acm-billing-cycle', label: 'Billing Cycle' },
    { id: 'acm-source', label: 'Source' }
  ];

  /** Services is the odd one out — a tick grid, not a field, so it is checked on its own. */
  private static readonly SERVICES_FIELD = 'acm-select-services';

  /**
   * Set by the first edit of anything. A flag rather than a diff against the opening values: the
   * question being asked is "did you touch this form", and someone who typed a character and
   * deleted it again still expects to be asked before it all goes.
   */
  readonly dirty = signal(false);

  /** Which way out is waiting on the confirm - the two fire different outputs, and the caller
   *  treats Cancel and the X differently (one is a decision, the other is a dismissal). */
  readonly pendingExit = signal<null | 'closed' | 'cancelled'>(null);

  /**
   * Every way out of the popup goes through here: the X, the backdrop, Escape and Cancel.
   *
   * An untouched form closes straight away - asking someone to confirm discarding nothing is the
   * kind of dialog people learn to click through without reading, which is how the warning stops
   * working on the day it matters.
   */
  requestExit(via: 'closed' | 'cancelled'): void {
    if (this.saving) return;
    if (!this.dirty()) {
      this.emitExit(via);
      return;
    }
    this.pendingExit.set(via);
  }

  confirmExit(): void {
    const via = this.pendingExit();
    this.pendingExit.set(null);
    if (via) this.emitExit(via);
  }

  cancelExit(): void {
    this.pendingExit.set(null);
  }

  private emitExit(via: 'closed' | 'cancelled'): void {
    if (via === 'cancelled') {
      this.cancelled.emit();
      return;
    }
    this.closed.emit();
  }

  /* —— Groups the live Clients form has and the design does not ——————————————————————
     Carried over deliberately: dropping them would stop those columns being written, and the
     fields exist because the Client Store expects them. */

  /** Sole Trader and Individual swap the company fields for a person. Read off the picked Client
   *  Type rather than a separate toggle, matching the live form. */
  readonly isIndividualType = computed(() => {
    const t = this.valueOf('acm-client-type').toLowerCase();
    return t.includes('sole') || t.includes('individual');
  });

  /**
   * Business and Book Start Date are asked of every client type except Individual.
   *
   * Deliberately not isIndividualType(), which also counts Sole Trader: a sole trader trades, so
   * it has both dates. Reading one flag for both rules would have made the popup pass a form the
   * host then refuses.
   */
  readonly requiresTradingDates = computed(() =>
    !this.valueOf('acm-client-type').toLowerCase().includes('individual')
  );

  /** "Other" turns the accounting system into a free-text field — the live form does the same. */
  readonly isOtherAccountingSystem = computed(() =>
    this.valueOf('acm-acc-system').toLowerCase() === 'other'
  );

  readonly officers = signal<readonly PmClientOfficer[]>([
    {
      key: 'officer-1',
      name: '',
      address: '',
      email: '',
      mobile: '',
      country: '',
      shareholder: false,
      appointedOn: '',
      terminatedOn: ''
    }
  ]);

  private officerSeq = 2;

  addOfficer(): void {
    this.dirty.set(true);
    this.officers.update((rows) => [
      ...rows,
      {
        key: `officer-${this.officerSeq++}`,
        name: '',
        address: '',
        email: '',
        mobile: '',
        country: '',
        shareholder: false,
        appointedOn: '',
        terminatedOn: ''
      }
    ]);
  }

  /** The last row stays: a client with no officers at all is not a state the form can save, and an
   *  empty block gives nothing to type into. */
  removeOfficer(key: string): void {
    if (this.officers().length <= 1) return;
    this.dirty.set(true);
    this.officers.update((rows) => rows.filter((r) => r.key !== key));
  }

  setOfficerField(key: string, field: keyof PmClientOfficer, value: string | boolean): void {
    this.dirty.set(true);
    this.officers.update((rows) =>
      rows.map((r) => (r.key === key ? { ...r, [field]: value } : r))
    );
    if (typeof value === 'string' && value.trim()) this.clearError(`acm-officer-${key}-${field}`);
  }

  /**
   * Officer details are asked for only when the client type has directors (Limited, Charity…).
   * An Individual, Sole Trader, LLP or Partnership keeps the section optional — otherwise every
   * such client would need a made-up director before it could be saved.
   */
  readonly officersRequired = computed(() => {
    const type = this.valueOf('acm-client-type').trim();
    return !!type && clientTypeSupportsDirectors(type);
  });

  /** Logical id of one officer's field, for Save's walk-to-the-gap and its error message. */
  officerFieldId(key: string, field: PmOfficerRequiredField): string {
    return `acm-officer-${key}-${field}`;
  }

  officerValue(row: PmClientOfficer, field: keyof PmClientOfficer): string {
    const v = row[field];
    return typeof v === 'string' ? v : '';
  }

  /**
   * Errors the host has decided on, keyed by the same field ids - a duplicate Client ID, a server
   * rejection. Kept apart from `errors`, which is this popup's own required-field checking: the
   * host's verdict must not be cleared by someone typing, and Save must not be able to overwrite
   * it with a fresh pass that knows nothing about it.
   */
  @Input() externalErrors: Record<string, string> = {};

  /* —— Sections ————————————————————————————————————————————————————————————————————
     Cards fold; they no longer save on their own. Each one used to carry its own Cancel / Save
     Details, which promised a partial save the form cannot make - a client is written in one
     request, so there is one Save Client at the foot and nothing else. */

  /** Which sections are expanded. Absent means open - a section the user has never touched should
   *  be showing, and seeding every id here would go stale the moment one is renamed. */
  readonly sectionOpen = signal<Record<string, boolean>>({});

  isSectionOpen(id: string): boolean {
    return this.sectionOpen()[id] ?? true;
  }

  setSectionOpen(id: string, open: boolean): void {
    this.sectionOpen.update((prev) => ({ ...prev, [id]: open }));
  }

  /** Every top-level section, in screen order. A collapsed section removes its body from the DOM,
   *  so a missing field inside one cannot be scrolled to until it is open again. */
  private static readonly SECTION_IDS: readonly string[] = [
    'client-details',
    'address',
    'due-diligence',
    'contact-person',
    'directors-and-shareholders',
    'services-and-staff',
    'portal-access'
  ];

  readonly errors = signal<Record<string, string>>({});

  /**
   * How many fields Save is waiting on in total, which is not the same as how many are marked.
   *
   * `errors` holds the one field Save is standing on; this holds the size of the problem. Marking
   * all eleven at once turned the form red end to end and gave no clue which one to answer first.
   */
  private readonly missingTotal = signal(0);

  /** Shown beside the button: the gap is usually several sections away, and a click whose only
   *  effect is a scroll somewhere else reads as a dead button. */
  readonly blockedCount = computed(() => this.missingTotal());

  errorFor(id: string): string {
    // The host's error wins: it knows things the form cannot, and it outlives a keystroke.
    return this.externalErrors[id] ?? this.errors()[id] ?? '';
  }

  /** The DOM id the field carries, kept apart from the logical id so the two cannot collide with
   *  anything else on the page when two popups are open. */
  fieldDomId(id: string): string {
    return `${id}-input`;
  }

  /**
   * Save is never disabled. A greyed-out button tells someone nothing about what is missing —
   * they are left hunting a 12-section form for the field that is holding it up. Pressing it
   * always does something: either it saves, or it walks them to the first gap and says what is
   * wrong there.
   */
  onSave(): void {
    const missing: Record<string, string> = {};
    for (const field of PmAddClientModalComponent.REQUIRED) {
      if (!this.valueOf(field.id).trim()) {
        missing[field.id] = `${field.label} is required`;
      }
    }
    // Conditional requirements - only asked for while their branch is on screen. A hidden field
    // that blocks Save is the worst kind: nothing to scroll to and nothing to correct.
    if (this.isIndividualType() && !this.valueOf('acm-individual-client').trim()) {
      missing['acm-individual-client'] = 'Individual Client is required';
    }
    if (this.requiresTradingDates()) {
      if (!this.valueOf('acm-business-start-date').trim()) {
        missing['acm-business-start-date'] = 'Business Start date is required';
      }
      if (!this.valueOf('acm-book-start-date').trim()) {
        missing['acm-book-start-date'] = 'Book Start date is required';
      }
    }
    if (this.officersRequired()) {
      for (const officer of this.officers()) {
        for (const { field, label } of OFFICER_REQUIRED_FIELDS) {
          if (!officer[field].trim()) missing[this.officerFieldId(officer.key, field)] = `${label} is required`;
        }
      }
    }
    if (this.isOtherAccountingSystem() && !this.valueOf('acm-acc-system-other').trim()) {
      missing['acm-acc-system-other'] = 'Name the accounting system';
    }
    if (this.createPortalUser()) {
      const pw = this.valueOf('acm-portal-password');
      const confirm = this.valueOf('acm-portal-password-confirm');
      if (!pw.trim()) missing['acm-portal-password'] = 'Password is required';
      if (!confirm.trim()) {
        missing['acm-portal-password-confirm'] = 'Confirm the password';
      } else if (pw && confirm !== pw) {
        missing['acm-portal-password-confirm'] = 'The two passwords do not match';
      }
    }

    // Practice-defined fields marked required are checked the same as the built-in ones.
    for (const field of this.customFields) {
      if (!field.required) continue;
      if (!this.customFieldValue(field.id).trim()) {
        missing[`acm-custom-${field.id}`] = `${field.name} is required`;
      }
    }

    if (this.selectedServices().size === 0) {
      missing[PmAddClientModalComponent.SERVICES_FIELD] = 'Pick at least one service';
    }

    this.missingTotal.set(Object.keys(missing).length);

    // A host error blocks the save too - it is a real reason the record cannot be written, and
    // silently saving over it would be worse than the old greyed-out button.
    const hostError = Object.keys(this.externalErrors).find((id) => this.externalErrors[id]);
    if (hostError) {
      this.focusField(hostError);
      return;
    }

    if (Object.keys(missing).length > 0) {
      // Unfold everything first: a collapsed section is not in the DOM, so the shell would find
      // nothing to compare or scroll to, and the click would look like it did nothing.
      this.sectionOpen.update((prev) => {
        const next = { ...prev };
        for (const id of PmAddClientModalComponent.SECTION_IDS) next[id] = true;
        return next;
      });
      // Only the field Save lands on is marked. Save stays on it until it is answered: press Save
      // again with it still blank and it is the same field, in the same place, with the same message.
      this.modal().goToFirstField(Object.keys(missing), this.fieldNav, (next) =>
        this.errors.set({ [next]: missing[next] })
      );
      return;
    }

    this.errors.set({});
    this.saved.emit();
  }

  /** How the shell finds and opens this popup's fields: DOM ids carry an -input suffix, and the
   *  open dropdown is held here rather than in each select. */
  private readonly fieldNav: PmModalFieldNav = {
    domId: (id) => this.fieldDomId(id),
    openDropdown: (id) => this.openDropdown.set(id)
  };

  private focusField(id: string): void {
    this.modal().goToField(id, this.fieldNav);
  }

  /**
   * Ticked services. Controlled the same way the fields are: a host that binds
   * [selectedServiceIds] owns the list and gets servicesChanged back, and the popup keeps its own
   * set only when nobody does.
   *
   * Without this the ticks lived here and nowhere else. The Clients page counts Services & Staff
   * complete from its own draft, so a form with three services ticked on screen still refused to
   * save, complaining that services were missing.
   */
  private readonly ownServices = signal<ReadonlySet<string>>(new Set());
  private readonly hostServices = signal<readonly string[] | null>(null);

  @Input() set selectedServiceIds(value: readonly string[] | null | undefined) {
    this.hostServices.set(value ?? null);
  }

  @Output() servicesChanged = new EventEmitter<string[]>();

  readonly selectedServices = computed<ReadonlySet<string>>(() => {
    const host = this.hostServices();
    return host ? new Set(host) : this.ownServices();
  });

  isServiceSelected(id: string): boolean {
    return this.selectedServices().has(id);
  }

  toggleService(id: string): void {
    this.dirty.set(true);
    const next = new Set(this.selectedServices());
    if (!next.delete(id)) next.add(id);
    if (this.hostServices()) {
      this.servicesChanged.emit([...next]);
      return;
    }
    this.ownServices.set(next);
  }

  /** Which password fields are currently readable. Per field, not one shared flag: someone
   *  checking a typo in one has no reason to expose the other. */
  readonly revealedPasswords = signal<ReadonlySet<string>>(new Set());

  isPasswordRevealed(id: string): boolean {
    return this.revealedPasswords().has(id);
  }

  togglePasswordReveal(id: string): void {
    this.revealedPasswords.update((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  /** Off by default: creating a portal login is a decision, not something to opt out of. */
  readonly createPortalUser = signal(false);

  togglePortalUser(): void {
    this.dirty.set(true);
    this.createPortalUser.update((v) => !v);
  }

}
